import { cn } from '@/lib/utils';
import { ChevronDown, Search, X } from 'lucide-react';
import { DatePicker } from '@/components/ui/DatePicker';

interface NoticeFilterBarProps {
  searchQuery: string;
  onSearchChange: (val: string) => void;
  selectedSubject: string;
  onSubjectChange: (val: string) => void;
  selectedSource: string;
  onSourceChange: (val: string) => void;
  selectedDate: string;
  onDateChange: (val: string) => void;
  sortBy: 'NEWEST' | 'OLDEST' | 'TITLE_AZ';
  onSortChange: (val: 'NEWEST' | 'OLDEST' | 'TITLE_AZ') => void;
  availableSubjects: string[];
  onClearFilters?: () => void;
}

export function NoticeFilterBar({
  searchQuery,
  onSearchChange,
  selectedSubject,
  onSubjectChange,
  selectedSource,
  onSourceChange,
  selectedDate,
  onDateChange,
  sortBy,
  onSortChange,
  availableSubjects,
  onClearFilters,
}: NoticeFilterBarProps) {
  const handleClear = () => {
    if (onClearFilters) {
      onClearFilters();
    } else {
      onSubjectChange('ALL');
      onSourceChange('ALL');
      onDateChange('');
      onSortChange('NEWEST');
    }
  };

  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-300/80 dark:border-slate-700 p-3 sm:p-3.5 shadow-xs flex flex-col xl:flex-row xl:items-end justify-between gap-3">
      {/* 1. Left Search Input */}
      <div className="w-full xl:max-w-xs 2xl:max-w-sm relative self-end">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search notices by title or content..."
            className="w-full bg-slate-50 dark:bg-slate-800/60 border border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 text-xs sm:text-sm text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-xl pl-10 pr-8 py-2 font-medium outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all h-[38px]"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-0.5 cursor-pointer"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* 2. Dropdown Filters + Date + Sort + Clear */}
      <div className="flex items-end gap-2.5 flex-1 min-w-0">
        <div className={cn('grid gap-2.5 flex-1 min-w-0', availableSubjects.length > 0 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3')}>
          {/* Class / Audience */}
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-base font-semibold text-slate-500 dark:text-slate-400">Class</span>
            <div className="relative">
              <select
                value={selectedSource}
                onChange={(e) => onSourceChange(e.target.value)}
                className="appearance-none w-full bg-slate-50 dark:bg-slate-800/60 border border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200 rounded-xl px-3 py-2 pr-7 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all cursor-pointer truncate h-[38px]"
              >
                <option value="ALL" className="bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200">
                  All Classes
                </option>
                <option value="CLASS" className="bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200">
                  Class Notices
                </option>
                <option value="INSTITUTE" className="bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200">
                  Institute Notices
                </option>
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            </div>
          </div>

          {/* Subject (when subjects available) */}
          {availableSubjects.length > 0 && (
            <div className="flex flex-col gap-1 min-w-0">
              <span className="text-base font-semibold text-slate-500 dark:text-slate-400">Subject</span>
              <div className="relative">
                <select
                  value={selectedSubject}
                  onChange={(e) => onSubjectChange(e.target.value)}
                  className="appearance-none w-full bg-slate-50 dark:bg-slate-800/60 border border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200 rounded-xl px-3 py-2 pr-7 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all cursor-pointer truncate h-[38px]"
                >
                  <option value="ALL" className="bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200">
                    All Subjects
                  </option>
                  {availableSubjects.map((sub) => (
                    <option key={sub} value={sub} className="bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200">
                      {sub}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Date Filter */}
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-base font-semibold text-slate-500 dark:text-slate-400">Date</span>
            <DatePicker
              label="Date"
              hideLabel
              size="sm"
              
              placeholder="Pick a date"
              value={selectedDate}
              onChange={(e) => onDateChange(e.target.value)}
              className="!h-[38px] !w-full !bg-slate-50 dark:!bg-slate-800/60 !border-slate-300 dark:!border-slate-600 hover:!border-slate-400 dark:hover:!border-slate-500 !text-xs sm:!text-sm !font-semibold !text-slate-700 dark:!text-slate-200 !rounded-xl !px-3 shadow-none"
            />
          </div>

          {/* Date Range / Sort */}
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-base font-semibold text-slate-500 dark:text-slate-400">Sort</span>
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => onSortChange(e.target.value as 'NEWEST' | 'OLDEST' | 'TITLE_AZ')}
                className="appearance-none w-full bg-slate-50 dark:bg-slate-800/60 border border-slate-300 dark:border-slate-600 hover:border-slate-400 dark:hover:border-slate-500 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-200 rounded-xl px-3 py-2 pr-7 outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all cursor-pointer truncate h-[38px]"
              >
                <option value="NEWEST" className="bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200">
                  All Dates (Newest)
                </option>
                <option value="OLDEST" className="bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200">
                  Oldest First
                </option>
                <option value="TITLE_AZ" className="bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200">
                  Title A-Z
                </option>
              </select>
              <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Clear Button (Red color for filters, small width) */}
        {(() => {
          const isFiltersActive =
            selectedSubject !== 'ALL' ||
            selectedSource !== 'ALL' ||
            selectedDate !== '' ||
            sortBy !== 'NEWEST';
          return (
            <div className="flex flex-col gap-1 shrink-0">
              <span className="text-base font-semibold invisible select-none" aria-hidden="true">Clear</span>
              <button
                type="button"
                disabled={!isFiltersActive}
                onClick={handleClear}
                className={`h-[38px] px-3.5 text-xs sm:text-sm font-semibold rounded-xl border transition-all shrink-0 flex items-center justify-center ${
                  isFiltersActive
                    ? 'text-red-600 dark:text-red-400 border-red-200 dark:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-950/30 hover:border-red-300 cursor-pointer'
                    : 'text-slate-400 dark:text-slate-600 border-slate-200 dark:border-slate-700 cursor-not-allowed opacity-50'
                }`}
              >
                Clear
              </button>
            </div>
          );
        })()}
      </div>
    </div>
  );
}